import React, { useEffect, useRef, useState } from 'react';
import { MenuItem, Paper, Popper, Select } from '@mui/material';

import { useDelayedOperation, usePrevious } from '@paragon/client-tools';

import { useAppSelector } from '../../hooks';
import { LayoutHierarchyPanel } from '../../interface/panels/layout-hierarchy-panel/LayoutHierarchyPanel';
import { ScopeDescription } from '../../interface/panels/layout-hierarchy-panel/ScopeDescription';
import { DEFAULT_TOOLBAR_POPPER_HIDE_DELAY, DEFAULT_TOOLBAR_POPPER_SHOW_DELAY } from '../config';

export function LayoutHierarchyToolbarLevelSelect() {
  const [configuredOpen, setConfiguredOpen] = useState(false);

  // A context menu being opened from the levels panel will unavoidably capture the mouse and trigger our popper to
  //   close. This "locked open" mode allows us to work around that.
  const [lockedOpenByContextMenu, setLockedOpenByContextMenu] = useState(false);
  const contextMenuTarget = useAppSelector((state) => state.viewport.rightClickedItem);
  const previousContextMenuTarget = usePrevious(contextMenuTarget);

  const showPopover = useDelayedOperation(() => void setConfiguredOpen(true), DEFAULT_TOOLBAR_POPPER_SHOW_DELAY);
  const hidePopover = useDelayedOperation(() => void setConfiguredOpen(false), DEFAULT_TOOLBAR_POPPER_HIDE_DELAY);

  const selectRef = useRef<HTMLSelectElement>(null);

  const open = configuredOpen || lockedOpenByContextMenu;

  // when locked open we must monitor the context menu so we can react when it closes
  useEffect(() => {
    if (!lockedOpenByContextMenu) return;

    if (contextMenuTarget !== previousContextMenuTarget && previousContextMenuTarget != null) {
      setLockedOpenByContextMenu(false);

      // effectively "close the popover if the mouse is no longer over it"
      showPopover.runImmediately();
      hidePopover.schedule();
    }
  }, [contextMenuTarget, hidePopover, lockedOpenByContextMenu, previousContextMenuTarget, showPopover]);

  return (
    <>
      <Select
        ref={selectRef}
        size="small"
        open={false}
        value="active-scope"
        onOpen={() => {
          showPopover.runImmediately();
          hidePopover.cancel();
        }}
        onClose={() => {
          hidePopover.runImmediately();
          showPopover.cancel();
        }}
        onPointerEnter={() => {
          showPopover.schedule();
          hidePopover.cancel();
        }}
        onPointerLeave={() => {
          hidePopover.schedule();
          showPopover.cancel();
        }}
      >
        <MenuItem value="active-scope">
          <ScopeDescription />
        </MenuItem>
      </Select>

      <Popper open={open} anchorEl={selectRef.current} placement="bottom">
        <Paper
          sx={{ width: '350px' }}
          onPointerEnter={() => {
            showPopover.schedule();
            hidePopover.cancel();
          }}
          onPointerLeave={() => {
            hidePopover.schedule();
            showPopover.cancel();
          }}
        >
          <LayoutHierarchyPanel
            onContextMenuDidOpen={() => {
              setLockedOpenByContextMenu(true);
            }}
            onClose={() => {
              showPopover.cancel();

              setConfiguredOpen(false);
              setLockedOpenByContextMenu(false);
            }}
          />
        </Paper>
      </Popper>
    </>
  );
}
